/**
 * Geography for the control-centre map, generated once from `world-atlas` (Natural
 * Earth 1:110m) and ISO 3166 — kept as data so the page does not ship a country-code
 * library to read 170 lines of it.
 *
 * - `NUMERIC_TO_ALPHA2`: the atlas identifies a country by its ISO numeric code, our
 *   sessions by its alpha-2 (`context.country_code`).
 * - `CENTROIDS`: where a country's bubble goes, [lng, lat]. The centroid of its
 *   *largest* polygon, not of the whole country — France's would otherwise land in the
 *   Atlantic, dragged there by French Guiana. Places too small for the 1:110m atlas
 *   (Réunion, the Antilles, Monaco, Singapore…) are added by hand: they call us, and a
 *   caller with no bubble is a caller the map pretends does not exist.
 */

export const NUMERIC_TO_ALPHA2: Record<string, string> = {
  "004": "AF",
  "008": "AL",
  "010": "AQ",
  "012": "DZ",
  "024": "AO",
  "031": "AZ",
  "032": "AR",
  "036": "AU",
  "040": "AT",
  "044": "BS",
  "050": "BD",
  "051": "AM",
  "056": "BE",
  "064": "BT",
  "068": "BO",
  "070": "BA",
  "072": "BW",
  "076": "BR",
  "084": "BZ",
  "090": "SB",
  "096": "BN",
  "100": "BG",
  "104": "MM",
  "108": "BI",
  "112": "BY",
  "116": "KH",
  "120": "CM",
  "124": "CA",
  "140": "CF",
  "144": "LK",
  "148": "TD",
  "152": "CL",
  "156": "CN",
  "158": "TW",
  "170": "CO",
  "178": "CG",
  "180": "CD",
  "188": "CR",
  "191": "HR",
  "192": "CU",
  "196": "CY",
  "203": "CZ",
  "204": "BJ",
  "208": "DK",
  "214": "DO",
  "218": "EC",
  "222": "SV",
  "226": "GQ",
  "231": "ET",
  "232": "ER",
  "233": "EE",
  "238": "FK",
  "242": "FJ",
  "246": "FI",
  "250": "FR",
  "260": "TF",
  "262": "DJ",
  "266": "GA",
  "268": "GE",
  "270": "GM",
  "275": "PS",
  "276": "DE",
  "288": "GH",
  "300": "GR",
  "304": "GL",
  "320": "GT",
  "324": "GN",
  "328": "GY",
  "332": "HT",
  "340": "HN",
  "348": "HU",
  "352": "IS",
  "356": "IN",
  "360": "ID",
  "364": "IR",
  "368": "IQ",
  "372": "IE",
  "376": "IL",
  "380": "IT",
  "384": "CI",
  "388": "JM",
  "392": "JP",
  "398": "KZ",
  "400": "JO",
  "404": "KE",
  "408": "KP",
  "410": "KR",
  "414": "KW",
  "417": "KG",
  "418": "LA",
  "422": "LB",
  "426": "LS",
  "428": "LV",
  "430": "LR",
  "434": "LY",
  "440": "LT",
  "442": "LU",
  "450": "MG",
  "454": "MW",
  "458": "MY",
  "466": "ML",
  "478": "MR",
  "484": "MX",
  "496": "MN",
  "498": "MD",
  "499": "ME",
  "504": "MA",
  "508": "MZ",
  "512": "OM",
  "516": "NA",
  "524": "NP",
  "528": "NL",
  "540": "NC",
  "548": "VU",
  "554": "NZ",
  "558": "NI",
  "562": "NE",
  "566": "NG",
  "578": "NO",
  "586": "PK",
  "591": "PA",
  "598": "PG",
  "600": "PY",
  "604": "PE",
  "608": "PH",
  "616": "PL",
  "620": "PT",
  "624": "GW",
  "626": "TL",
  "630": "PR",
  "634": "QA",
  "642": "RO",
  "643": "RU",
  "646": "RW",
  "682": "SA",
  "686": "SN",
  "688": "RS",
  "694": "SL",
  "703": "SK",
  "704": "VN",
  "705": "SI",
  "706": "SO",
  "710": "ZA",
  "716": "ZW",
  "724": "ES",
  "728": "SS",
  "729": "SD",
  "732": "EH",
  "740": "SR",
  "748": "SZ",
  "752": "SE",
  "756": "CH",
  "760": "SY",
  "762": "TJ",
  "764": "TH",
  "768": "TG",
  "780": "TT",
  "784": "AE",
  "788": "TN",
  "792": "TR",
  "795": "TM",
  "800": "UG",
  "804": "UA",
  "807": "MK",
  "818": "EG",
  "826": "GB",
  "834": "TZ",
  "840": "US",
  "854": "BF",
  "858": "UY",
  "860": "UZ",
  "862": "VE",
  "887": "YE",
  "894": "ZM",
};

export const CENTROIDS: Record<string, [number, number]> = {
  "AD": [1.5,42.5],
  "AE": [54.2,23.9],
  "AF": [66,33.8],
  "AL": [20,41.1],
  "AM": [45,40.2],
  "AO": [17.5,-12.3],
  "AQ": [83.1,-84.8],
  "AR": [-64.7,-34.6],
  "AT": [14.1,47.6],
  "AU": [134.2,-25.6],
  "AZ": [47.7,40.3],
  "BA": [17.8,44.2],
  "BB": [-59.55,13.2],
  "BD": [90.3,23.8],
  "BE": [4.6,50.7],
  "BF": [-1.8,12.3],
  "BG": [25.2,42.8],
  "BH": [50.6,26],
  "BI": [29.9,-3.4],
  "BJ": [2.3,9.6],
  "BL": [-62.8,17.9],
  "BN": [114.9,4.7],
  "BO": [-64.7,-16.7],
  "BR": [-53.2,-10.7],
  "BS": [-77.9,24.5],
  "BT": [90.5,27.4],
  "BW": [23.8,-22.1],
  "BY": [28,53.5],
  "BZ": [-88.7,17.2],
  "CA": [-97.9,58.4],
  "CD": [23.6,-2.8],
  "CF": [20.4,6.5],
  "CG": [15.1,-0.8],
  "CH": [8.1,46.8],
  "CI": [-5.6,7.6],
  "CL": [-71.2,-35.9],
  "CM": [12.6,5.7],
  "CN": [103.4,36.7],
  "CO": [-73.1,3.9],
  "CR": [-84.2,10],
  "CU": [-78.9,21.6],
  "CV": [-23.6,15.1],
  "CY": [33,34.9],
  "CZ": [15.3,49.8],
  "DE": [10.3,51.1],
  "DJ": [42.5,11.8],
  "DK": [9.3,56.2],
  "DO": [-70.5,18.9],
  "DZ": [2.6,28.1],
  "EC": [-78.4,-1.5],
  "EE": [25.8,58.6],
  "EG": [29.9,26.5],
  "EH": [-12.2,24.3],
  "ER": [38.7,15.4],
  "ES": [-3.6,40.3],
  "ET": [39.6,8.6],
  "FI": [26.1,64.3],
  "FJ": [178,-17.8],
  "FK": [-59.4,-51.7],
  "FR": [2.3,46.6],
  "GA": [11.7,-0.6],
  "GB": [-2.6,53.8],
  "GE": [43.5,42.2],
  "GF": [-53.1,3.9],
  "GG": [-2.58,49.45],
  "GH": [-1.2,7.9],
  "GI": [-5.35,36.14],
  "GL": [-42,73.2],
  "GM": [-15.4,13.5],
  "GN": [-11.1,10.5],
  "GP": [-61.6,16.2],
  "GQ": [10.4,1.6],
  "GR": [22.5,39.3],
  "GT": [-90.4,15.7],
  "GW": [-15.1,12],
  "GY": [-59,4.8],
  "HK": [114.2,22.3],
  "HN": [-86.6,14.8],
  "HR": [16.6,45],
  "HT": [-72.7,18.9],
  "HU": [19.3,47.2],
  "ID": [114,-0.3],
  "IE": [-8,53.2],
  "IL": [35,31.5],
  "IM": [-4.5,54.2],
  "IN": [79.5,22.8],
  "IQ": [43.8,33],
  "IR": [54.5,32.5],
  "IS": [-18.8,65.1],
  "IT": [12.4,43.4],
  "JE": [-2.13,49.21],
  "JM": [-77.3,18.1],
  "JO": [36.8,31.2],
  "JP": [136.7,36],
  "KE": [37.8,0.6],
  "KG": [74.6,41.5],
  "KH": [104.9,12.7],
  "KM": [43.9,-11.9],
  "KP": [127.1,40.1],
  "KR": [127.8,36.4],
  "KW": [47.6,29.3],
  "KZ": [67.2,48.4],
  "LA": [103.8,18.4],
  "LB": [35.9,33.9],
  "LI": [9.55,47.15],
  "LK": [80.7,7.7],
  "LR": [-9.4,6.4],
  "LS": [28.2,-29.6],
  "LT": [23.9,55.3],
  "LU": [6,49.8],
  "LV": [24.8,56.8],
  "LY": [18,27],
  "MA": [-8.7,29.8],
  "MC": [7.4,43.7],
  "MD": [28.4,47.2],
  "ME": [19.3,42.8],
  "MF": [-63.1,18.1],
  "MG": [46.7,-19.3],
  "MK": [21.7,41.6],
  "ML": [-3.6,17.2],
  "MM": [96.5,20.9],
  "MN": [103,47],
  "MO": [113.5,22.2],
  "MQ": [-61,14.6],
  "MR": [-10.3,20.2],
  "MT": [14.4,35.9],
  "MU": [57.6,-20.3],
  "MV": [73.5,3.2],
  "MW": [34.2,-13.2],
  "MX": [-102.2,23.9],
  "MY": [114.7,3.5],
  "MZ": [35.5,-17.2],
  "NA": [17.1,-22],
  "NC": [165.5,-21.3],
  "NE": [9.3,17.3],
  "NG": [8,9.5],
  "NI": [-85,12.8],
  "NL": [5.5,52.3],
  "NO": [12.4,64.1],
  "NP": [84,28.2],
  "NZ": [170.6,-44],
  "OM": [56.1,20.6],
  "PA": [-80.1,8.5],
  "PE": [-74.4,-9.1],
  "PF": [-149.4,-17.7],
  "PG": [144.3,-6.6],
  "PH": [121.6,15.7],
  "PK": [69.2,29.9],
  "PL": [19.3,52.1],
  "PM": [-56.3,46.9],
  "PR": [-66.5,18.2],
  "PS": [35.3,31.9],
  "PT": [-8.1,39.6],
  "PY": [-58.4,-23.2],
  "QA": [51.2,25.3],
  "RE": [55.5,-21.1],
  "RO": [25,45.9],
  "RS": [20.8,44.2],
  "RU": [95.7,65.9],
  "RW": [29.9,-2],
  "SA": [44.6,24.1],
  "SB": [159.1,-7.9],
  "SC": [55.5,-4.7],
  "SD": [29.8,16],
  "SE": [16.1,62.4],
  "SG": [103.8,1.35],
  "SI": [14.9,46.1],
  "SK": [19.5,48.7],
  "SL": [-11.8,8.5],
  "SM": [12.45,43.94],
  "SN": [-14.5,14.4],
  "SO": [45.7,4.7],
  "SR": [-55.9,4.1],
  "SS": [30.2,7.3],
  "SV": [-88.9,13.7],
  "SY": [38.5,35],
  "SZ": [31.4,-26.5],
  "TD": [18.6,15.3],
  "TF": [69.5,-49.3],
  "TG": [1,8.4],
  "TH": [101,15],
  "TJ": [71,38.6],
  "TL": [126,-8.8],
  "TM": [59.3,39.1],
  "TN": [9.5,34.1],
  "TR": [35.4,39.1],
  "TT": [-61.3,10.4],
  "TW": [121,23.7],
  "TZ": [34.7,-6.3],
  "UA": [31.3,49.2],
  "UG": [32.4,1.3],
  "US": [-98.7,39.9],
  "UY": [-56,-32.8],
  "UZ": [63.4,41.8],
  "VA": [12.45,41.9],
  "VE": [-66.2,7.2],
  "VN": [106.3,16.6],
  "VU": [166.9,-15.2],
  "WF": [-176.2,-13.3],
  "XK": [20.9,42.6],
  "YE": [47.5,15.9],
  "YT": [45.2,-12.8],
  "ZA": [25.2,-28.9],
  "ZM": [27.8,-13.4],
  "ZW": [29.8,-18.9],
};
